import React, { useState, useRef, useEffect } from 'react';
import { Info, Trash2, Menu, Lock, Link2, X } from 'lucide-react';
import { useHistory, useLocation } from 'react-router-dom';
import Tooltip from '../addentities/Tooltip.js';
import CharacterLimitedTextarea from '../../components/CharacterLimitedTextarea';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal.js';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt.js';
import AlertNotification from '../../components/AlertNotification.js';
import LinkStockInstancesModal from './LinkStockInstancesModal.js'; // ← NEW
import apiHelper from '../../apiHelper.js';
import noImage from '../../assets/img/brand/noImage.png'
import emptyStockInstance from '../../assets/img/emptyStockInstance.png'

export default function PlatformManageOrder() {
    const history = useHistory();
    const location = useLocation();
    const isCancelled = location.state?.order?.status === 'Cancelled' || location.state?.order?.status === 'Canceled';
    console.log('Order status:', location.state?.order?.status);

    const getInitialFormData = () => {
        const order = location.state?.order;
        const rawSettlement = order?.settlementPrice;
        let parsedSettlement = '';
        if (rawSettlement && rawSettlement !== '-') {
            const numVal = parseFloat(String(rawSettlement).replace(/[^\d.]/g, ''));
            if (!isNaN(numVal) && numVal > 0) {
                parsedSettlement = String(numVal);
            }
        }
        return {
            settlement_price: parsedSettlement,
            currency: 'SGD',
            linked_stock_instance: '',
            notes: order?.notes || '',
        };
    };

    const order = location.state?.order;
    const orderItems = order?.products || [];

    const [formData, setFormData] = useState(getInitialFormData);
    const [settlementFocused, setSettlementFocused] = useState(false);
    const [errors, setErrors] = useState({ settlement_price: '' });
    const [touchedFields, setTouchedFields] = useState({ settlement_price: false });
    const [disabled, setDisabled] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [alert, setAlert] = useState(null);
    const [showItemAlert, setShowItemAlert] = useState(false);
    const [discardModalOpen, setDiscardModalOpen] = useState(false);
    const [pendingNavLocation, setPendingNavLocation] = useState(null);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkedInstances, setLinkedInstances] = useState([]); // [{id, qty}]
    const [originalLinkedInstances, setOriginalLinkedInstances] = useState([]);
    const discardRef = useRef(null);
    const dragIndexRef = useRef(null);
    const platformLabel = location.state?.platformLabel || 'Platform';

    useEffect(() => {
        function handleClickOutside(event) {
            if (discardRef.current && !discardRef.current.contains(event.target)) {
                setDiscardModalOpen(false);
            }
        }
        if (discardModalOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [discardModalOpen]);

    // Block in-app navigation when there are unsaved changes
    useEffect(() => {
        if (!isDirty || isSaving) return;
        const unblock = history.block((location, action) => {
            setPendingNavLocation(location);
            setDiscardModalOpen(true);
            return false; // block navigation, show discard modal
        });
        return () => unblock();
    }, [isDirty, isSaving, history]);

    // Warn on browser/tab close when there are unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty && !isSaving) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty, isSaving]);

    const showAlert = (type, action, recordName) => {
        setAlert({ type, action, recordName });
        setShowItemAlert(true);
        setTimeout(() => setShowItemAlert(false), 5000);
    };

    const formatSettlementDisplay = (val) => {
        if (val === '' || val == null) return '';
        const num = parseFloat(String(val).replace(/,/g, ''));
        if (isNaN(num)) return val;
        return num.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const clean = name === 'settlement_price'
            ? value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1')
            : value;
        setFormData((prev) => ({ ...prev, [name]: clean }));
        if (!isDirty) setIsDirty(true);
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        return { settlement_price: '' };
    };

    const handleSave = async () => {
        setIsSaving(true);
        setDisabled(true);
        setTouchedFields({ settlement_price: true });

        const nextErrors = validateForm();
        setErrors(nextErrors);

        const hasErrors = Object.values(nextErrors).some(Boolean);
        if (hasErrors) {
            setDisabled(false);
            setIsSaving(false);
            showAlert('error', 'create', 'Manage Order');
            return;
        }

        try {
            console.log('Form data:', formData);
            console.log('Linked instances:', linkedInstances);

            const order = location.state?.order;
            console.log('Order from location state:', order);

            // ✅ FIX 1: correct order ID
            const orderId = order?.platformOrderId;

            if (!orderId) {
                console.error("Order ID missing");
                showAlert('error', 'create', 'Order ID missing');
                return;
            }

            const currentIds = linkedInstances.map(i => i.id);
            const originalIds = originalLinkedInstances.map(i => i.id);

            // For new links, platform_order_item_id comes from the order's products
            // (requires backend to expose platform_order_item_id in the products array)
            const defaultOrderItemId = order?.products?.[0]?.platform_order_item_id;

            // ✅ 1. Linked (status = 1)
            const linkedPayload = linkedInstances.map((item, index) => ({
                platform_order_stockinstance_link_id: item.platform_order_stockinstance_link_id,
                platform_order_item_id: item.orderItemId || defaultOrderItemId,
                stock_instances_id: item.stockInstanceId ?? item.id,
                linked_quantity: item.qty || 1,
                sort_order: index + 1,
                status: 1,
                branch_id: item.branchId,
                platform_name: platformLabel || 'N/A'
            }));

            // ✅ 2. Unlinked (status = 2)
            const unlinkedPayload = originalLinkedInstances
                .filter(item => !currentIds.includes(item.id))
                .map(item => ({
                    platform_order_stockinstance_link_id: item.platform_order_stockinstance_link_id,
                    platform_order_item_id: item.orderItemId || defaultOrderItemId,
                    stock_instances_id: item.stockInstanceId ?? item.id,
                    linked_quantity: item.qty || 1,
                    status: 2,
                    branch_id: item.branchId,
                    platform_name: platformLabel || 'N/A'
                }));

            // ✅ Final payload
            const payload = {
                settlement_price: formData.settlement_price,
                notes: formData.notes,
                linked_stockinstance_qty: linkedInstances.reduce(
                    (sum, item) => sum + (item.qty || 0),
                    0
                ),
                stock_links: [
                    ...linkedPayload,
                    ...unlinkedPayload
                ]
            };

            console.log('Final payload:', payload);

            const response = await apiHelper(
                `sales/platform-order/${orderId}/link/`,
                'POST',
                payload
            );

            if (!response || !response.ok) {
                const errText = response ? await response.text() : 'No response';
                console.error('Save failed — status:', response?.status, 'body:', errText);
                throw new Error("API failed");
            }

            const responseData = await response.json().catch(() => null);
            console.log('Save response:', responseData);

            // ✅ Success
            setDisabled(false);
            setIsSaving(false);
            setIsDirty(false);
            sessionStorage.setItem(
                "global_alert",
                JSON.stringify({
                    type: "success",
                    action: "update",
                    recordName: "Manage Order"
                })
            );

            // ✅ go back
            history.goBack();

        } catch (error) {
            console.error(error);
            setDisabled(false);
            setIsSaving(false);
            showAlert('error', 'create', 'Manage Order');
        }
    };

    const handleDiscard = () => {
        if (!isDirty || isSaving) {
            history.goBack();
            return;
        }
        setDiscardModalOpen(true);
    };

    const handleDiscardConfirm = (confirm) => {
        setDiscardModalOpen(false);
        if (confirm) {
            setIsDirty(false);
            setFormData({ settlement_price: '', currency: 'SGD', linked_stock_instance: '', notes: '' });
            if (pendingNavLocation) {
                const dest = pendingNavLocation;
                setPendingNavLocation(null);
                history.push(dest.pathname, dest.state);
            } else {
                history.goBack();
            }
        } else {
            setPendingNavLocation(null);
        }
    };

    const handleCancelDiscard = () => {
        setDiscardModalOpen(false);
        setPendingNavLocation(null);
    };

    // ── Pre-populate linkedInstances from existing order data ─────────────────
    useEffect(() => {
        const order = location.state?.order;

        if (order?.stockInstances && order.stockInstances.length > 0) {
            const sorted = [...order.stockInstances].sort(
                (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
            );
            const mapped = sorted.map((si, index) => ({
                id: si.platform_order_stockinstance_link_id ?? `${si.stock_instance_id}_order_${index}`,
                stockInstanceId: si.stock_instance_id,
                name: si.stockName,
                variant: si.stockSku,
                model_number: si.stockModelNumber || si.model_number || "",
                condition: si.condition,
                grade: si.condition_type,
                serialNo: si.skuId,
                branch: si.branch,
                branchId: si.branch_id ?? si.branchId,
                costPrice: si.price,
                currency: 'SGD',
                qty: si.instanceQty || 1,
                image: si.image || si.stock_image || si.stockImage || null,
                orderItemId: si.platform_order_item_id,
                platform_order_stockinstance_link_id: si.platform_order_stockinstance_link_id,
                sort_order: si.sort_order ?? index + 1,
            }));
            setLinkedInstances(mapped);
            setOriginalLinkedInstances(mapped);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // ─────────────────────────────────────────────────────────────────────────

    // ── Auto-unlink all StockInstances when order is Cancelled ───────────────
    useEffect(() => {
        if (isCancelled && linkedInstances.length > 0) {
            setLinkedInstances([]);
        }
    }, [isCancelled]);
    // ─────────────────────────────────────────────────────────────────────────

    // ── Handle confirmed linked instances ────────────────────────────────────
    const handleLinkConfirm = (selected) => {
        setLinkedInstances(selected);
        if (!isDirty) setIsDirty(true);
        setLinkModalOpen(false);
    };
    // ─────────────────────────────────────────────────────────────────────────


    const handleBackToPlatform = () => {
        history.goBack();
    };

    const panelHeader = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 16px',
        background: '#E9ECEF',
        borderBottom: '1px solid #E9ECEF',
    };

    const itemCountBadge = (count) => (
        <span style={{ fontSize: 12, color: '#6C757D', fontFamily: 'Montserrat' }}>
            {count} {count <= 1 ? 'item' : 'items'}
        </span>
    );

    return (
        <>
            <div
                className="company-body main-company-body-inner new-look"
                style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
            >
                {showItemAlert && alert && (
                    <AlertNotification
                        type={alert.type}
                        action={alert.action}
                        recordName={alert.recordName}
                        onClose={() => setShowItemAlert(false)}
                    />
                )}

                {/* Breadcrumb */}
                <div className="breadcrumb">
                    <span className="breadcrumb-item">Order - Platform </span>
                    <span
                        className="breadcrumb-item"
                        onClick={handleBackToPlatform}
                        style={{ color: '#6C757D', cursor: 'pointer' }}
                    >
                        {platformLabel}{' '}
                    </span>
                    <span className="breadcrumb-item">Manage Order</span>
                </div>

                {/* Header */}
                <div className='edit-organisation-info'>
                    <div className='edit-organisation-left'>
                        <h1>Manage Order</h1>
                    </div>
                    <div className='discard-save-organisation-right d-flex align-items-center gap-2'>
                        <button
                            className="dark-button"
                            type='button'
                            onClick={handleDiscard}
                            style={{ width: '100px', padding: '8px', height: '34px' }}
                        >
                            Discard
                        </button>
                        <DiscardConfirmationModal
                            isOpen={discardModalOpen}
                            onCancel={handleCancelDiscard}
                            onConfirm={handleDiscardConfirm}
                            entityName="Manage Order"
                        />
                        <button
                            className="dark-border"
                            type='button'
                            onClick={handleSave}
                            disabled={disabled}
                            style={{ width: '100px', padding: '8px', height: '34px' }}
                        >
                            Save
                        </button>
                    </div>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '24px' }}>
                    <div className="info-section">

                        {/* ── Prices card ── */}
                        <div className="info-card new-info-overflow-auto">
                            <div className="info-header">
                                <h4 className="info-title">Prices</h4>
                            </div>
                            <div className="info-content d-block">
                                <div className='grid-info-adding'>
                                    <div className='row gappingcls-added'>
                                        <div className='col-lg-6 col-md-6 col-12'>
                                            <div className="info-inner">
                                                <label
                                                    htmlFor="settlementPrice"
                                                    className="form-label d-flex align-items-center gap-2"
                                                >
                                                    Settlement Price
                                                    {/* <span className='error-color'>*</span> */}
                                                    <Tooltip
                                                        title="Settlement Price"
                                                        body="The Price we actually received from selling this Order (after deducting platform fees, discount and any other costs). Manually inputted on ERP."
                                                    >
                                                        <Info size={16} className="info-icon" />
                                                    </Tooltip>
                                                </label>

                                                <div
                                                    className={`form-control d-flex align-items-center p-0 ${errors.settlement_price ? 'err-color' : ''
                                                        }`}
                                                    style={{
                                                        overflow: 'hidden',
                                                        height: '39px',
                                                        display: 'flex',
                                                        border: `1px solid ${errors.settlement_price ? '' : '#6c757d'}`,
                                                        outline: 'none',
                                                        boxShadow: 'none'
                                                    }}
                                                >
                                                    {/* Currency */}
                                                    <div
                                                        style={{
                                                            width: '60px',
                                                            background: '#FFF',
                                                            borderRight: '1px solid #212529',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '14px',
                                                            height: '100%',
                                                            padding: '10px',
                                                            fontFamily: 'Montserrat'
                                                        }}
                                                    >
                                                        {formData.currency}
                                                    </div>

                                                    {/* Input + Icon wrapper */}
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            flex: 1,
                                                            paddingRight: '8px',
                                                        }}
                                                    >
                                                        <input
                                                            type="text"
                                                            id="settlementPrice"
                                                            name="settlement_price"
                                                            value={settlementFocused ? formData.settlement_price : formatSettlementDisplay(formData.settlement_price)}
                                                            onChange={handleChange}
                                                            onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                                                            onFocus={() => setSettlementFocused(true)}
                                                            onBlur={() => setSettlementFocused(false)}
                                                            placeholder="0.00"
                                                            style={{
                                                                flex: 1,
                                                                border: 'none',
                                                                outline: 'none',
                                                                background: 'transparent',
                                                                padding: '10px',
                                                                fontSize: '14px',
                                                                height: '100%',
                                                            }}
                                                        />

                                                        {/* SVG inside input area */}
                                                        {formData.settlement_price && (
                                                            <span
                                                                onClick={() => {
                                                                    setFormData(prev => ({ ...prev, settlement_price: '' }));
                                                                    setIsDirty(true);
                                                                }}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                }}
                                                            >
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="12"
                                                                    height="12"
                                                                    viewBox="0 0 12 12"
                                                                    fill="none"
                                                                >
                                                                    <path
                                                                        d="M9 3L3 9M3 3L9 9"
                                                                        stroke="#6C757D"
                                                                        strokeWidth="2"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                    />
                                                                </svg>
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {/* {errors.settlement_price && (
                                                    <p style={{ color: '#DC3545', fontSize: '12px', marginTop: '4px', marginBottom: 0 }}>
                                                        {errors.settlement_price}
                                                    </p>
                                                )} */}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Linked StockInstances card ── */}
                        {!isCancelled && (
                            <div className="info-card new-info-overflow-auto">
                                <div className="info-header">
                                    <h4 className="info-title">Linked StockInstances</h4>
                                </div>
                                <div className="info-content d-block">
                                    <div style={{ background: '#FFF', borderRadius: '12px', padding: '20px', boxShadow: '0px 1px 4px 0px rgba(0,0,0,0.10)', border: '1px solid #E9ECEF' }}>
                                    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>

                                        {/* ── LEFT: Ordered Items ── */}
                                        <div style={{ flex: 2, border: '1px solid #E9ECEF', borderRadius: '12px', overflow: 'hidden', background: '#FFF' }}>
                                            <div style={panelHeader}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <Lock size={14} color="#6C757D" />
                                                    <span style={{ fontWeight: 700, fontSize: 13, fontFamily: 'Montserrat', color: '#212529' }}>
                                                        Ordered Items
                                                    </span>
                                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#6C757D', fontFamily: 'Montserrat' }}>
                                                        ID:{' '}
                                                        <span style={{ color: '#6C757D', textDecoration: 'underline', cursor: 'pointer' }}>
                                                            {order?.id || order?.platformOrderId || '—'}
                                                        </span>
                                                    </span>
                                                </div>
                                                {itemCountBadge(orderItems.reduce((sum, item) => sum + (Number(item?.qty) || 0), 0))}
                                            </div>

                                            {orderItems.map((item, index) => (
                                                <div
                                                    key={index}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        padding: '12px 16px',
                                                        background: '#F8F9FA',
                                                        borderBottom: index < orderItems.length - 1 ? '1px solid #E9ECEF' : 'none',
                                                        gap: '12px',
                                                        minHeight: 80,
                                                        boxSizing: 'border-box',
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                                        {/* <img
                                                            src={item?.image || noImage}
                                                            alt="product"
                                                            style={{ width: 40, height: 40, borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }}
                                                        /> */}
                                                        <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                                            <div style={{ fontWeight: 400, fontSize: 12, color: '#212529', fontFamily: 'Montserrat', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                {item?.name}
                                                            </div>
                                                            <div style={{ fontSize: 10, fontWeight: 400, color: '#6C757D', marginTop: 2, fontFamily: 'Montserrat', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                Variation: {item?.variation}
                                                            </div>
                                                            <div style={{ fontSize: 10, fontWeight: 400, color: '#6C757D', marginTop: 1, fontFamily: 'Montserrat', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                Platform Price: SGD {(() => { const n = parseFloat(String(item?.unitPrice ?? '').replace(/[^0-9.]/g, '')); return isNaN(n) ? '—' : n.toFixed(2); })()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <span style={{ fontWeight: 400, fontSize: 12, color: '#212529', flexShrink: 0, fontFamily: 'Montserrat' }}>
                                                        {item?.qty}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>

                                        {/* ── Divider ── */}
                                        <div style={{ width: '1px', background: '#E9ECEF', alignSelf: 'stretch', flexShrink: 0 }} />

                                        {/* ── RIGHT: Linked StockInstances ── */}
                                        <div style={{ flex: 3, border: '1px solid #E9ECEF', borderRadius: '12px', overflow: 'hidden', background: '#FFF' }}>
                                            <div style={panelHeader}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <Link2 size={14} color="#6C757D" />
                                                    <span style={{ fontWeight: 700, fontSize: 13, fontFamily: 'Montserrat', color: '#212529' }}>
                                                        Linked StockInstances
                                                    </span>
                                                </div>
                                                {itemCountBadge(linkedInstances.reduce((sum, item) => sum + (Number(item?.qty) || 0), 0))}
                                            </div>

                                            {linkedInstances.length === 0 ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 24px', textAlign: 'center' }}>
                                                    <img src={emptyStockInstance} alt="No linked StockInstances" style={{ width: 80, height: 78, objectFit: 'contain', marginBottom: 20 }} />
                                                    <div style={{ fontWeight: 600, fontSize: 20, color: '#212529', fontFamily: 'Montserrat', marginBottom: 6 }}>
                                                        No linked StockInstances
                                                    </div>
                                                    <div style={{ fontSize: 14, fontWeight: 400, color: '#6C757D', fontFamily: 'Montserrat', marginBottom: 20 }}>
                                                        No StockInstances Linked to this Order
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setLinkModalOpen(true)}
                                                        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', height: '34px', borderRadius: '8px', border: '1px solid #212529', background: '#FFF', fontSize: '12px', fontWeight: '600', fontFamily: 'Montserrat', cursor: 'pointer', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)' }}
                                                    >
                                                        + Link StockInstance
                                                    </button>
                                                </div>
                                            ) : (
                                                <div>
                                                    {linkedInstances.map((item, index) => (
                                                        <div
                                                            key={item.id || index}
                                                            draggable
                                                            onDragStart={() => { dragIndexRef.current = index; }}
                                                            onDragOver={e => e.preventDefault()}
                                                            onDrop={() => {
                                                                const from = dragIndexRef.current;
                                                                if (from === null || from === index) return;
                                                                setLinkedInstances(prev => {
                                                                    const next = [...prev];
                                                                    const [moved] = next.splice(from, 1);
                                                                    next.splice(index, 0, moved);
                                                                    return next;
                                                                });
                                                                dragIndexRef.current = null;
                                                                if (!isDirty) setIsDirty(true);
                                                            }}
                                                            style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #E9ECEF', gap: '10px', height: 80, boxSizing: 'border-box' }}
                                                        >
                                                            <Trash2
                                                                size={16}
                                                                style={{ cursor: 'pointer', color: '#6C757D', flexShrink: 0 }}
                                                                onClick={() => {
                                                                    setLinkedInstances(prev => prev.filter((_, i) => i !== index));
                                                                    if (!isDirty) setIsDirty(true);
                                                                }}
                                                            />
                                                            <img
                                                                src={item?.image || noImage}
                                                                alt="product"
                                                                style={{ width: 40, height: 40, borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }}
                                                            />
                                                            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                                                <div style={{ fontWeight: 400, fontSize: 12, color: '#212529', fontFamily: 'Montserrat', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                    {item?.name}
                                                                </div>
                                                                <div style={{ fontSize: 10, fontWeight: 400, color: '#6C757D', marginTop: 1, fontFamily: 'Montserrat', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                    Variation: {item?.variant}{item?.model_number ? ` | ${item.model_number}` : ''}
                                                                </div>
                                                                <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                                                                    {item?.condition && (
                                                                        <span style={{ background: '#FFEB00', borderRadius: 99, padding: '2px 6px', fontSize: 9, fontWeight: 600, color: '#212529', fontFamily: 'Montserrat' }}>
                                                                            {item.condition}
                                                                        </span>
                                                                    )}
                                                                    {item?.grade && (
                                                                        <span style={{ background: '#FFEB00', borderRadius: 99, padding: '2px 6px', fontSize: 9, fontWeight: 600, color: '#212529', fontFamily: 'Montserrat' }}>
                                                                            {item.grade}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div style={{ textAlign: 'left', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                                                <div style={{ fontSize: 12, color: '#212529', fontFamily: 'Montserrat', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {item?.serialNo}
                                                                </div>
                                                                <div style={{ fontSize: 11, color: '#6C757D', marginTop: 1, fontFamily: 'Montserrat', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {item?.branch} | SGD {item?.costPrice != null ? Number(item.costPrice).toFixed(2) : '—'}
                                                                </div>
                                                            </div>
                                                            <div style={{ width: 20, minWidth: 20, maxWidth: 20, textAlign: 'center', fontSize: 13, fontWeight: 500, fontFamily: 'Montserrat', flexShrink: 0 }}>
                                                                {item?.qty}
                                                            </div>
                                                            <Menu size={16} color="#6C757D" style={{ cursor: 'grab', flexShrink: 0 }} />
                                                        </div>
                                                    ))}
                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px' }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => setLinkModalOpen(true)}
                                                            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', height: '34px', borderRadius: '8px', border: '1px solid #212529', background: '#FFF', fontSize: '12px', fontWeight: '600', fontFamily: 'Montserrat', cursor: 'pointer', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)' }}
                                                        >
                                                            + Link StockInstance
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                    </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── Notes card ── */}
                        <div className="info-card new-info-overflow-auto">
                            <div className="info-header">
                                <h4 className="info-title">Notes</h4>
                            </div>
                            <div className="info-content d-block">
                                <textarea
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    rows={4}
                                    style={{
                                        width: '100%',
                                        border: '1px solid #CED4DA',
                                        borderRadius: 8,
                                        padding: '10px 12px',
                                        fontSize: 14,
                                        fontFamily: 'Montserrat',
                                        resize: 'vertical',
                                        outline: 'none',
                                        color: '#212529',
                                    }}
                                />
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            {/* ── Link StockInstances Modal ── */}
            <LinkStockInstancesModal
                isOpen={linkModalOpen}
                onClose={() => setLinkModalOpen(false)}
                onConfirm={handleLinkConfirm}
                preSelected={linkedInstances}
            />
        </>
    );
}
